import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, Lock, Mail, Briefcase } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Profile = () => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.put(`${API_URL}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      'EMPLOYEE': 'Employee',
      'MANAGER': 'Manager',
      'HR_ADMIN': 'HR Admin'
    };
    return labels[role] || role;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl tracking-tight font-semibold text-slate-900" data-testid="profile-title">Profile Settings</h1>
        <p className="text-sm text-slate-700 mt-2">Manage your account information and security</p>
      </div>

      {/* User Information */}
      <Card className="border border-slate-200 bg-white rounded-sm p-6">
        <h2 className="text-2xl tracking-tight font-semibold text-slate-900 mb-6">Account Information</h2>
        
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-200 rounded-sm">
            <div className="w-12 h-12 rounded-full bg-[#1E40AF] flex items-center justify-center text-white font-semibold text-lg">
              {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <User className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Full Name</span>
              </div>
              <div className="text-lg font-semibold text-slate-900">{user?.name}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-slate-500" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Email Address</span>
              </div>
              <div className="text-slate-900">{user?.email}</div>
            </div>

            <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <Briefcase className="h-4 w-4 text-slate-500" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Role</span>
              </div>
              <div className="text-slate-900">{getRoleLabel(user?.role)}</div>
            </div>
          </div>

          {user?.department && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <Briefcase className="h-4 w-4 text-slate-500" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Department</span>
              </div>
              <div className="text-slate-900">{user?.department}</div>
            </div>
          )}

          {user?.manager_name && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-slate-500" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Reports To</span>
              </div>
              <div className="text-slate-900">{user?.manager_name}</div>
            </div>
          )}
        </div>
      </Card>

      {/* Change Password */}
      <Card className="border border-slate-200 bg-white rounded-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <Lock className="h-6 w-6 text-slate-700" />
          <h2 className="text-2xl tracking-tight font-semibold text-slate-900">Change Password</h2>
        </div>
        
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <Label htmlFor="current-password" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
              Current Password
            </Label>
            <Input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="border-slate-200 focus:ring-2 focus:ring-blue-600 focus:outline-none rounded-sm"
              data-testid="current-password-input"
              placeholder="Enter current password"
            />
          </div>

          <div>
            <Label htmlFor="new-password" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
              New Password
            </Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="border-slate-200 focus:ring-2 focus:ring-blue-600 focus:outline-none rounded-sm"
              data-testid="new-password-input"
              placeholder="Enter new password (min 6 characters)"
            />
          </div>

          <div>
            <Label htmlFor="confirm-password" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
              Confirm New Password
            </Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="border-slate-200 focus:ring-2 focus:ring-blue-600 focus:outline-none rounded-sm"
              data-testid="confirm-password-input"
              placeholder="Confirm new password"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-sm"
              data-testid="change-password-button"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
              }}
              className="border-slate-200 rounded-sm"
            >
              Clear
            </Button>
          </div>
        </form>

        <div className="mt-6 p-4 border border-slate-200 bg-slate-50 rounded-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Password Requirements</p>
          <ul className="text-sm text-slate-700 space-y-1">
            <li>• Minimum 6 characters</li>
            <li>• Must enter current password for verification</li>
            <li>• New password must be confirmed</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default Profile;
